// The inbox has two halves: entries addressed to this reader, and entries the
// platform published to everyone. The API calls them `user` and `platform`; the
// UI calls them "Your activity" and "Platform".
export const NOTIFICATION_SCOPES = ['user', 'platform'] as const;

export type NotificationScope = (typeof NOTIFICATION_SCOPES)[number];

export function isNotificationScope(value: string): value is NotificationScope {
  return (NOTIFICATION_SCOPES as readonly string[]).includes(value);
}

export function toNotificationScope(value: string | null | undefined): NotificationScope {
  return value != null && isNotificationScope(value) ? value : 'user';
}

// Held in a map rather than in literal t() calls, so notification-i18n.spec
// checks both maps explicitly — its scraper only sees t('ns.KEY').
export const TAB_LABEL_KEYS = {
  user: 'notifications.TAB_USER',
  platform: 'notifications.TAB_PLATFORM',
} as const satisfies Readonly<Record<NotificationScope, string>>;

// An allow-list, not a computed key. A kind with no entry renders no category
// at all, so a producer can ship a new kind without leaking `request_played`
// into the meta line, and the label can follow later.
export const KIND_LABEL_KEYS = {
  announcement: 'notifications.KIND_ANNOUNCEMENT',
  release: 'notifications.KIND_RELEASE',
  // Both board kinds share one chip. The chip names the area of the app an
  // entry came from, the way "Announcement" and "Release notes" do; whether it
  // was a status change or a reply is what the title already says.
  feature_request_status: 'notifications.KIND_FEATURE_REQUEST',
  feature_request_comment: 'notifications.KIND_FEATURE_REQUEST',
} as const satisfies Readonly<Record<string, string>>;

export function notificationKindLabelKey(kind: string): string | null {
  return Object.hasOwn(KIND_LABEL_KEYS, kind)
    ? KIND_LABEL_KEYS[kind as keyof typeof KIND_LABEL_KEYS]
    : null;
}

type UnreadCounts = { unreadUser: number; unreadPlatform: number };

// Which tab to open on. Your own activity is the point of the split, so it
// wins unless it has nothing to show and the other half does.
export function defaultNotificationScope(counts: UnreadCounts | null): NotificationScope {
  if (!counts) return 'user';

  return counts.unreadUser === 0 && counts.unreadPlatform > 0 ? 'platform' : 'user';
}
